import { useMemo } from 'react';
import { bucketForMode } from '../lib/modes.js';

// useFilteredSpots applies the layered filter model to the live spot store.
//
// Model (mirrors HolyCluster's UX, locked in SCHEMA + plan):
//   1. Quick filters (bands / modes / dx-continent / spotter-continent) —
//      empty axis = no restriction; multiple axes are AND'd, chips within
//      an axis are OR'd.
//   2. Show-only — spot must match at least one entry on either DX or
//      spotter side, OR be matched by an alert (alerts override).
//   3. Hide — spot is excluded if it matches any entry, UNLESS an alert hits.
//
// Returns { filtered, alertIds } where alertIds is a Set of spot ids that
// matched an alert entry (so the UI can render them with a highlight).
export function useFilteredSpots(spots, settings) {
  return useMemo(() => {
    const bandSet = setOf(settings.bands);
    // settings.modes holds bucket tokens (CW / Phone / Digi). Map each
    // spot's granular mode to its bucket and check membership.
    const modeBucketSet = setOf(settings.modes);
    const dxContSet = setOf(settings.dxContinents);
    const spContSet = setOf(settings.spotterContinents);
    const dxCallNeedle = (settings.dxCallFilter || '').toUpperCase().trim();

    const showOnly = settings.showOnlyOn ? settings.showOnly : [];
    const hide     = settings.hideOn     ? settings.hide     : [];
    const alerts   = settings.alertsOn   ? settings.alerts   : [];

    const alertIds = new Set();
    const filtered = [];

    for (const s of spots) {
      // Quick filters
      if (bandSet && !bandSet.has(s.band)) continue;
      if (modeBucketSet) {
        const b = bucketForMode(s.mode);
        if (!b || !modeBucketSet.has(b)) continue;
      }
      if (dxContSet && !dxContSet.has(s.dx_continent)) continue;
      if (spContSet && !spContSet.has(s.spotter_continent)) continue;
      if (dxCallNeedle && !(s.dx_callsign || '').toUpperCase().includes(dxCallNeedle)) continue;

      const isAlert = alerts.length > 0 && matchesAny(s, alerts);
      if (isAlert) alertIds.add(s.id);

      // Hide list — alerts override.
      if (!isAlert && hide.length > 0 && matchesAny(s, hide)) continue;

      // Show-only — alerts override (always shown).
      if (!isAlert && showOnly.length > 0 && !matchesAny(s, showOnly)) continue;

      filtered.push(s);
    }
    return { filtered, alertIds };
  }, [spots, settings]);
}

function setOf(arr) {
  if (!arr || arr.length === 0) return null;
  return new Set(arr);
}

// matchesAny returns true if any of the entries (uppercased prefix-match)
// hits dx_callsign, spotter_callsign, dx_country, or dx_continent.
function matchesAny(spot, entries) {
  const dxCall = spot.dx_callsign || '';
  const spCall = spot.spotter_callsign || '';
  const dxCountry = (spot.dx_country || '').toUpperCase();
  for (const raw of entries) {
    const e = raw.toUpperCase().trim();
    if (!e) continue;
    if (dxCall.startsWith(e) || spCall.startsWith(e)) return true;
    if (dxCountry.includes(e)) return true;
  }
  return false;
}
